import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { type SimpleShowRes } from '@on3/api/src';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import styles from './ShowsCarousel.module.scss';

interface ShowsCarouselProps {
  shows: SimpleShowRes[];
  title?: string;
}

export const ShowsCarousel = ({
  title = 'Podcasts & Shows',
  shows,
}: ShowsCarouselProps) => {
  const { formatHref } = useDynamicRoute();
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);

  const [current, setCurrent] = useState(0);

  const handlePrev = () => setCurrent((prev) => prev - 1);
  const handleNext = () => setCurrent((prev) => (prev + 1) % shows.length);

  useEffect(() => {
    if (ref.current) {
      const slider = ref.current;
      const slides = slider.children as HTMLCollectionOf<HTMLElement>;

      const { scrollWidth, clientWidth, scrollLeft } = slider;

      const containerOffset = slides[0].offsetLeft; // the x padding offset of the slider container
      const currentSlideLeft = slides[current].offsetLeft;

      const slideLeft = currentSlideLeft - containerOffset;
      const maxScrollLeft = scrollWidth - clientWidth;

      let newScrollLeft = slideLeft;

      if (slideLeft > maxScrollLeft) {
        if (scrollLeft !== maxScrollLeft) {
          newScrollLeft = maxScrollLeft;
        } else {
          newScrollLeft = 0;
          setCurrent(0);
        }
      }

      slider.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  }, [current]);

  return (
    <div className={styles.block}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.carousel}>
        <div data-ui="slider" ref={ref}>
          {shows.map((show, idx) => (
            <Link
              className={styles.show}
              href={formatHref({
                path:
                  pathname +
                  createUrl(['shows', slugify(`${show.name}-${show.key}`)]),
              })}
              key={idx}
            >
              <Avatar
                altText={show.asset?.altText}
                domain={show.asset?.domain}
                height={180}
                source={show.asset?.source}
                width={180}
              />
            </Link>
          ))}
        </div>
      </div>
      <footer className={styles.footer}>
        <Link
          className={styles.allShowsLink}
          href={formatHref({
            path: pathname + '/shows/',
          })}
          useSiteColor
        >
          All Podcasts & Shows
        </Link>
        <nav data-ui="slider-nav">
          <button
            aria-label="Previous"
            disabled={current === 0}
            onClick={handlePrev}
          >
            <ChevronLeft />
          </button>
          <button aria-label="Next" onClick={handleNext}>
            <ChevronRight />
          </button>
        </nav>
      </footer>
    </div>
  );
};
